"use client"

import { zodResolver } from "@hookform/resolvers/zod"
import { httpsCallable } from "firebase/functions"
import { useTranslations } from "next-intl"
import { Controller, useForm } from "react-hook-form"
import { Button } from "@/components/ui/button"
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field"
import { Input } from "@/components/ui/input"
import { defaultFormValidation } from "@/lib/actions/formProps"
import { getFirebaseFunctions } from "@/lib/auth/getFirebaseFunctions"
import { trackSso } from "../analytics/events"
import { mapFirebaseFunctionsError } from "../utils/mapFirebaseFunctionsError"
import { type SsoEmailInput, ssoEmailSchema } from "../validators/sso"

type SsoRedirectRequest = {
  email: string
  redirectAfterSiroLogin: string
  redirectAfterIDPLogin: string
}

type SsoRedirectResponse = {
  authorizationUrl: string
}

type SsoFieldErrorKey = "invalidEmail"

// The zod schema emits locale-free error codes (e.g. `invalidEmail`). The
// render site maps them onto translated messages so the schema stays usable
// on the server without binding to a locale.
const FIELD_ERROR_CODES = new Set<SsoFieldErrorKey>(["invalidEmail"])
const isFieldErrorKey = (value: string): value is SsoFieldErrorKey =>
  (FIELD_ERROR_CODES as Set<string>).has(value)

export type SsoEmailFormProps = {
  /**
   * Where to land the user after the whole SSO round-trip completes
   * (post-callback, post-cookie-mint). Forwarded to the backend as
   * `redirectAfterSiroLogin` so the callable can echo it back from the
   * callback exchange. Same shape as `/login`'s `?redirect=` param —
   * caller is responsible for sanitizing (single-slash same-origin path).
   */
  redirectAfterSiroLogin?: string
}

export const SsoEmailForm = ({
  redirectAfterSiroLogin = "/",
}: SsoEmailFormProps) => {
  const t = useTranslations("auth.sso")
  const tErrors = useTranslations("auth.sso.errors")

  const form = useForm<SsoEmailInput>({
    resolver: zodResolver(ssoEmailSchema),
    defaultValues: { email: "" },
    ...defaultFormValidation,
  })

  const onSubmit = form.handleSubmit(async ({ email }) => {
    form.clearErrors("root")
    trackSso("SSO Page Login", { email })
    try {
      const redirectFunc = httpsCallable<
        SsoRedirectRequest,
        SsoRedirectResponse
      >(getFirebaseFunctions(), "sso-onCallRedirect")
      const redirectAfterIDPLogin = `${window.location.origin}/login/sso/callback`
      // Backend echoes `redirectAfterSiroLogin` back as `redirectAfterLogin`
      // on the exchange. Send an absolute URL to match the CC contract (and
      // to give the callback handler a fully-qualified URL to sanitize).
      const absoluteRedirectAfterSiroLogin = redirectAfterSiroLogin.startsWith(
        "http",
      )
        ? redirectAfterSiroLogin
        : `${window.location.origin}${redirectAfterSiroLogin}`
      const { data } = await redirectFunc({
        email,
        redirectAfterSiroLogin: absoluteRedirectAfterSiroLogin,
        redirectAfterIDPLogin,
      })
      // Hard navigation — we're leaving the app for the IdP. Don't use
      // `router.replace`; Next would try to client-route to an external URL.
      window.location.href = data.authorizationUrl
    } catch (error) {
      trackSso("SSO Page Login Error", {
        email,
        error: (error as Error).message,
      })
      form.setError("root.serverError", {
        message: tErrors(
          mapFirebaseFunctionsError(error, {
            // Backend returns `functions.https.HttpsError('not-found', ...)`
            // when the email's domain isn't configured for SSO.
            "functions/not-found": "unknownEmail" as const,
          }),
        ),
      })
    }
  })

  const isSubmitting = form.formState.isSubmitting
  const rootError = form.formState.errors.root?.serverError

  return (
    <form onSubmit={onSubmit} noValidate>
      <FieldGroup>
        <Controller
          name="email"
          control={form.control}
          render={({ field, fieldState }) => (
            <Field data-invalid={fieldState.invalid || undefined}>
              <FieldLabel htmlFor="sso-email">{t("email.label")}</FieldLabel>
              <Input
                {...field}
                id="sso-email"
                type="email"
                inputMode="email"
                autoComplete="email"
                enterKeyHint="go"
                placeholder={t("email.placeholder")}
                aria-invalid={fieldState.invalid || undefined}
                disabled={isSubmitting}
              />
              {fieldState.invalid && fieldState.error?.message && (
                <FieldError
                  errors={[
                    {
                      message: isFieldErrorKey(fieldState.error.message)
                        ? tErrors(fieldState.error.message)
                        : fieldState.error.message,
                    },
                  ]}
                />
              )}
            </Field>
          )}
        />
        {rootError?.message && (
          <FieldError errors={[{ message: rootError.message }]} />
        )}
        <Button type="submit" disabled={isSubmitting} className="w-full">
          {isSubmitting ? t("submitting") : t("submit")}
        </Button>
      </FieldGroup>
    </form>
  )
}
